import React, { useMemo } from 'react';
import Card from 'src/components/common/Card.redesign';
import BillingAndPaymentCycle from 'src/models/BillingAndPaymentCycle';
import Landman from 'src/models/Landman';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import ProjectStaff from 'src/models/ProjectStaff';
import User from 'src/models/User';
import { ContextMenuAction } from 'src/components/common/ContextMenu';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import ProjectWorkClassification from 'src/models/ProjectWorkClassification';
import RHFCheckBox from 'src/components/controls/tempRHFControls/RHFCheckBox';

interface IStaffingLandmanCard {
    projectStaffInfo: ProjectStaff;
    landman: Landman;
    index: number;
    pcapmList: User[];
    projectAdminStaff: ProjectAdministratorStaff[];
    billingCycles: BillingAndPaymentCycle[];
    workClassifications: ProjectWorkClassification[];
    showDialog: ContextMenuAction[];
    hideDialog: () => void;
    newStaffingInfo?: ProjectStaff;
    deStaffedListener?: (data: boolean) => void;
    updateCardListener?: (data: boolean) => void;
}

const currentYear = new Date().getFullYear();

// Stable style objects at module level — new object literals on every render defeat React.memo
const SLC_NOWRAP: React.CSSProperties = { whiteSpace: 'nowrap' };
const SLC_CARD_BODY: React.CSSProperties = { height: '20px', display: 'flex', justifyContent: 'left', alignItems: 'left', margin: '0 3rem 0 3rem', gap: '3%' };
const SLC_CARD_MARGIN: React.CSSProperties = { margin: '0' };
const SLC_CHECKBOX_ROW: React.CSSProperties = { display: 'flex', gap: '0.5rem', marginLeft: 'auto', marginBottom: '1rem', width: '40%' };

const StaffedLandmanCard = (props: IStaffingLandmanCard) => {
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    // Memoized so the array reference stays stable between renders — prevents Card's
    // React.memo from being defeated by a new array on every render
    const editLandmen: ContextMenuAction[] = useMemo(() => [
        {
            text: 'Edit Staff Info',
            action: () =>
                dialogContext.project.addEditLandmanInfo(
                    props.projectStaffInfo,
                    props.billingCycles,
                    props.pcapmList,
                    props.projectAdminStaff,
                    props.workClassifications,
                    props.updateCardListener
                ),
        },
        {
            text: 'De-Staff Personnel',
            action: () =>
                dialogContext.project.addRemoveLandman(props.projectStaffInfo, props.landman, props.workClassifications, props.deStaffedListener),
        },
    ], [dialogContext, props.projectStaffInfo, props.billingCycles, props.pcapmList,
        props.projectAdminStaff, props.workClassifications, props.updateCardListener,
        props.landman, props.deStaffedListener]);

    const startCycle = useMemo(() => {
        const match = props?.billingCycles?.find((c: BillingAndPaymentCycle) => c.id === props?.projectStaffInfo?.startBillingCycle?.id);
        if (!match) { return ''; }
        return match.cycleNumber <= 9 ? `${currentYear} - 0${match.cycleNumber}` : `${currentYear} - ${match.cycleNumber}`;
    }, [props.billingCycles, props.projectStaffInfo?.startBillingCycle?.id]);

    const endCycle = useMemo(() => {
        const match = props?.billingCycles?.find((c: BillingAndPaymentCycle) => c.id === props?.projectStaffInfo?.endBillingCycle?.id);
        if (!match) { return ''; }
        return match.cycleNumber <= 9 ? `${currentYear} - 0${match.cycleNumber}` : `${currentYear} - ${match.cycleNumber}`;
    }, [props.billingCycles, props.projectStaffInfo?.endBillingCycle?.id]);

/*     const pcamp = () => {
        return props.pcapmList
            .filter((item) => item.id === props?.projectStaffInfo?.pcapmId)
            .map((item) => {
                return `${item.firstName} ${item.lastName}`;
            });
    };

    const pm = () => {
        return props.pcapmList
            .filter((item) => item.id === props?.projectStaffInfo?.projectManagerId)
            .map((item) => {
                return `${item.firstName} ${item.lastName}`;
            });
    }; */

    const workClassification = useMemo(() => {
        const match = props.workClassifications?.find((item) => item.id === props?.projectStaffInfo?.workClassificationId);
        return match ? `${match.alias} - ${match.name}` : '';
    }, [props.workClassifications, props.projectStaffInfo?.workClassificationId]);

    return (
        <>
            <Card
                collapse={true}
                badge={props.projectStaffInfo?.pendingCycle ? 'Pending new cycle' : ''}
                contextActions={editLandmen}
                newContext={true}
                showMenu={true}
            >
                <div style={SLC_CARD_MARGIN} className="mb-1 mt-1" key={`Staff${props.index}`}>
                    <div style={SLC_CARD_BODY}>
                        <div style={{ width: '130px' }}>
                            <label style={SLC_NOWRAP}>{`${props.landman.firstName} ${props.landman.lastName}`}</label>
                        </div>
                        <div style={{ width: '100px' }}>
                            <label style={SLC_NOWRAP}>{startCycle}</label>
                        </div>
                        <div style={{ width: '120px' }}>
                            <label style={SLC_NOWRAP}>{endCycle}</label>
                        </div>
                        {/*<div style={{ width: '80px' }}>
                            <label style={{whiteSpace: 'nowrap'}}>{props?.projectStaffInfo?.overridePayRate}</label>
                        </div>
                            <div style={{ width: '100px' }}>
                        <label style={{whiteSpace: 'nowrap'}}>{props?.projectStaffInfo?.overrideBillRate}</label>
                        </div>
                        <div style={{ width: '100px' }}>
                            <label style={{ whiteSpace: 'nowrap' }} >{pcamp()}</label>
                        </div>
                        <div style={{ width: '100px' }}>
                            <label style={{ whiteSpace: 'nowrap' }} >{pm()}</label>
                        </div>*/}
                        <div style={{ width: '140px' }}>
                            <label style={SLC_NOWRAP}>{workClassification}</label>
                        </div>

                        <div style={SLC_CHECKBOX_ROW}>
                            <RHFCheckBox
                                name={'ownershipAccess'}
                                label={'Ownership'}
                                labelStyle={SLC_NOWRAP}
                                defaultVal={!!props?.projectStaffInfo?.ownershipAccess ? 'checked' : 'unchecked'}
                                checked={props?.projectStaffInfo?.ownershipAccess}
                                style={SLC_NOWRAP}
                                readOnly={true}
                            />
                            <RHFCheckBox
                                name={'contractAccess'}
                                label={'Contracts'}
                                labelStyle={SLC_NOWRAP}
                                defaultVal={!!props?.projectStaffInfo?.contractAccess ? 'checked' : 'unchecked'}
                                checked={props?.projectStaffInfo?.contractAccess}
                                style={SLC_NOWRAP}
                                readOnly={true}
                            />
                            <RHFCheckBox
                                name={'reportAccess'}
                                label={'Reports'}
                                labelStyle={SLC_NOWRAP}
                                defaultVal={!!props?.projectStaffInfo?.reportAccess ? 'checked' : 'unchecked'}
                                checked={props?.projectStaffInfo?.reportAccess}
                                style={SLC_NOWRAP}
                                readOnly={true}
                            />
                            <RHFCheckBox
                                name={'gisAccess'}
                                label={'GIS'}
                                labelStyle={SLC_NOWRAP}
                                defaultVal={!!props?.projectStaffInfo?.gisAccess ? 'checked' : 'unchecked'}
                                checked={props?.projectStaffInfo?.gisAccess}
                                style={SLC_NOWRAP}
                                readOnly={true}
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </>
    );
};

export default React.memo(StaffedLandmanCard);



// WEBPACK FOOTER //
// ./src/components/project/sections/StaffedLandmanCard.tsx